import React, { useEffect, useState } from 'react';
import { SortableTable } from '@bambuser-ui';
import { usePrevious } from 'hooks/usePrevious.js';

/**
 * @callback onRowsFiltered
 * @param {Object[]} rows
 */

/**
 * @callback FilterExpression
 * @param {any} value
 * @returns {boolean}
 */

/**
 * @typedef FilterableTableSpecificProps
 * @property {string}                                 [searchQuery] If provided, the rows of the table will be filtered on this string
 * @property {{[columnId: string]: FilterExpression}} [filter] A filter expression can be passed per matching id of the table rows, example: myColumnToFilterOn: (value) => value >= 10
 * @property {onRowsFiltered}                         [onRowsFiltered] Callback for acting on row being filtered
 * @property {Array}                                  [nonVisibleKeysToSearch] Key(s) not related to a headCell that still should be searchable. Only full string match will apply to not confuse the user.
 */

/**
 * @typedef {FilterableTableSpecificProps & import('../sortableTable.js').SortableTableProps} FilterableTableProps
 */

/**
 * @param {FilterableTableProps} props
 */
const FilterableTable = ({
  searchQuery = '',
  filter,
  onRowsFiltered,
  nonVisibleKeysToSearch,
  ...sortableTableOptions
}) => {
  const [searchableRows, setSearchableRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const previousFilteredRows = usePrevious(filteredRows);

  const { rows = [], headCells = [] } = sortableTableOptions;

  useEffect(() => {
    if (!headCells.length) return;
    if (!rows.length && !searchableRows.length) return;

    const searchableKeys = headCells.map(cell => cell.id);
    const searchableValues = rows.map((row) => {
      row._searchValue = searchableKeys.reduce((searchableString, key) => searchableString + row[key]?.toString()?.toLowerCase(), '');
      return row;
    });
    setSearchableRows(searchableValues);
  }, [rows, headCells]);

  useEffect(() => {
    let matchingRows;

    if (filter) {
      matchingRows = searchableRows.filter(row => {
        return !Object.keys(filter).some(key => {
          return !filter[key](row[key]);
        });
      });
    }

    matchingRows = (matchingRows || searchableRows).filter(row => {
      const queryLowerCase = searchQuery?.toLowerCase();
      const hasMatchingVisibleValues = row._searchValue.includes(queryLowerCase);
      const hasMatchingHiddenValues = nonVisibleKeysToSearch?.some(key => row[key]?.toLowerCase() === queryLowerCase);

      return hasMatchingVisibleValues || hasMatchingHiddenValues;
    });

    setFilteredRows(matchingRows);
  }, [searchQuery, filter, searchableRows]);

  useEffect(() => {
    const filteredRowsChanged = previousFilteredRows?.length !== filteredRows?.length;

    if (filteredRowsChanged && onRowsFiltered) onRowsFiltered(filteredRows);
  }, [filteredRows]);

  return <SortableTable {...sortableTableOptions} rows={filteredRows} />;
};

export default FilterableTable;
